/**
 * The conversation with the AI mission operator, and the gate its output has to
 * pass through.
 *
 * Two surfaces in one dock on purpose. The thread is where intent is expressed
 * and revised; the review gate directly beneath it is where a proposal becomes
 * an order. Splitting them across screens is how a reviewer ends up approving
 * something they discussed rather than something they read.
 *
 * VOICE IS AN INPUT METHOD, NEVER A DECISION.
 *
 * Dictation writes into the message box and nowhere else. It cannot approve,
 * execute, reject or undo, and there is no code path from a transcript to any
 * of those calls. The specific failure this prevents: "approve" and "improve"
 * are one phoneme apart, and so are "reject" and "re-check". A recognizer that
 * is 95% accurate on a quiet desk is far worse than that beside a generator, in
 * wind, through a headset — and the one word it must never get wrong is the one
 * that puts a route on a real aircraft. Approval is a deliberate click, and for
 * a real fleet a set of typed callsigns on top of it.
 */

import { useEffect, useRef, useState } from 'react';

import type { CopilotLiveStep, CopilotStep } from '@/api/copilot';
import { useVoiceInput } from '@/features/command/useVoiceInput';
import ProposalReview from '@/features/copilot/ProposalReview';
import { usePlanDock } from '@/features/planning/planDocks';
import {
  copilotActions,
  useActiveProposal,
  useCopilotBusy,
  useCopilotError,
  useCopilotLiveSteps,
  useCopilotMessages,
  type CopilotMessage,
} from '@/stores/copilotStore';

const OK = 'oklch(0.78 0.15 155)';
const WARN = 'oklch(0.80 0.15 85)';
const DANGER = 'oklch(0.72 0.19 28)';

/** The tool trace of the turn that produced this proposal — the latest one, so a
 *  revision shows how the revision was reached rather than the original. */
function stepsFor(messages: CopilotMessage[], proposalId: string): CopilotStep[] {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i];
    if (message?.proposalId === proposalId && message.steps) return message.steps;
  }
  return [];
}

/**
 * What a step that did not succeed should be called, and how loudly.
 *
 * It used to say FAILED in red, which is the wrong word and the wrong colour.
 * Every step in this trace that is not ok is a tool that DECLINED and told the
 * model why — "sustainment needs a consumption rate", "no such zone" — and the
 * model then read that and carried on. A genuine crash never reaches this list
 * at all: it is caught in the loop, logged, and fed back as its own step, and
 * anything that escapes even that ends the turn rather than appearing here.
 *
 * So red FAILED taught an operator to distrust a system that was working
 * exactly as designed: a refusal is the tool being careful, and the turn beside
 * it usually succeeded. Amber REFUSED says what happened, and the reason is
 * already on the same line.
 */
export function stepLabel(step: CopilotStep): string {
  return step.ok ? 'OK' : 'REFUSED';
}

export function stepTone(step: CopilotStep): string {
  return step.ok ? OK : WARN;
}

function StepTrace({ message }: { message: CopilotMessage }) {
  if (!message.steps?.length) return null;
  return (
    <ol className="mt-1.5 flex flex-col gap-0.5 border-t border-white/10 pt-1.5">
      {message.steps.map((step, index) => (
        <li key={index} className="flex items-center gap-2 text-[9.5px]">
          <span className="tnum text-white/30">{index + 1}</span>
          <span className="font-mono text-white/65">{step.tool}</span>
          <span className="ml-auto label-mono text-[8px]" style={{ color: stepTone(step) }}>
            {stepLabel(step)}
          </span>
        </li>
      ))}
    </ol>
  );
}

/** A tool result on one line. The server already summarised it; this only stops
 *  a 1200-character summary from taking the panel over. */
function oneLine(result: unknown): string {
  const text = typeof result === 'string' ? result : JSON.stringify(result ?? '');
  const flat = text.replace(/\s+/g, ' ').trim();
  return flat.length > 80 ? `${flat.slice(0, 80)}…` : flat;
}

export /**
 * A planning turn is a dozen model calls and runs to a couple of minutes. A
 * bare "Working…" for that long reads as a hang — an operator watching one
 * reported it as no response at all.
 *
 * So the turn narrates itself: each tool call appears as the server makes it,
 * under an elapsed count. The elapsed count alone proved the panel was alive;
 * the trace is what makes the wait legible, because "plan.divide" on screen at
 * 90 s is the difference between a slow plan and a stuck one.
 *
 * `steps` is empty on the fallback path, where the server buffers the whole
 * turn. The elapsed count still runs, so that path degrades to what shipped
 * before rather than to nothing.
 */
function WorkingFor({ steps }: { steps: CopilotLiveStep[] }): JSX.Element {
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    const started = Date.now();
    const timer = window.setInterval(
      () => setSeconds(Math.round((Date.now() - started) / 1000)),
      1000,
    );
    return () => window.clearInterval(timer);
  }, []);

  return (
    <div
      className="mr-2 rounded-[12px] border border-white/10 bg-black/25 px-3 py-2.5"
      role="status"
      aria-live="polite"
    >
      <span className="px-pulse text-[11px] text-white/45">
        Planning… {seconds}s
      </span>
      <span className="ml-2 text-[10px] text-white/30">
        a full plan usually takes 60–150s
      </span>
      {steps.length ? (
        <ol className="mt-1.5 flex flex-col gap-0.5 border-t border-white/10 pt-1.5">
          {steps.map((step) => (
            <li key={step.index} className="flex items-baseline gap-2 text-[9.5px]">
              <span className="tnum text-white/30">{step.index + 1}</span>
              <span className="font-mono text-white/70">{step.tool}</span>
              <span
                aria-hidden="true"
                className="min-w-0 flex-1 truncate text-right font-mono text-white/35"
                title={oneLine(step.result)}
              >
                {oneLine(step.result)}
              </span>
              <span className="label-mono text-[8px]" style={{ color: stepTone(step) }}>
                {stepLabel(step)}
              </span>
            </li>
          ))}
        </ol>
      ) : null}
    </div>
  );
}

function CopilotDock() {
  const { open, setOpen } = usePlanDock('copilot');
  const [input, setInput] = useState('');
  const messages = useCopilotMessages();
  const proposal = useActiveProposal();
  const busy = useCopilotBusy();
  const liveSteps = useCopilotLiveSteps();
  const error = useCopilotError();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);

  // Dictation appends to the message box. This is the ONLY thing a transcript
  // is allowed to touch — see the file header for why.
  const voice = useVoiceInput((text) => setInput((current) => (current ? `${current} ${text}` : text)));

  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ block: 'nearest' });
  }, [messages, busy, liveSteps, open]);

  const send = () => {
    const trimmed = input.trim();
    if (!trimmed || busy) return;
    setInput('');
    void copilotActions.send(trimmed);
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open the AI mission operator"
        className="plan-copilot-pill glass-pill pointer-events-auto absolute right-[calc(var(--plan-r-w)+26px)] top-[86px] z-10 rounded-full px-3 py-2 font-mono text-[9px] font-bold tracking-wide text-white/70"
      >
        AI OPERATOR{proposal ? ' · 1 PROPOSAL' : ''}
      </button>
    );
  }

  return (
    <aside
      aria-label="AI mission operator"
      className="plan-copilot glass scroll-thin pointer-events-auto absolute right-[calc(var(--plan-r-w)+26px)] top-[86px] bottom-[244px] z-10 flex w-[var(--plan-dock-w)] flex-col overflow-hidden rounded-[20px]"
    >
      <div className="flex flex-none items-center gap-2 px-3.5 pt-3 pb-2">
        <span className="label-mono text-[10px]">AI MISSION OPERATOR</span>
        <span className="label-mono text-[8px] text-white/40">PROPOSES · NEVER COMMANDS</span>
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label="Close the AI mission operator"
          className="ml-auto label-mono text-[8px] text-white/45 hover:text-white"
        >
          HIDE
        </button>
      </div>

      {error ? (
        <p className="flex-none px-3.5 pb-1.5 text-[10px] leading-relaxed" style={{ color: WARN }}>
          {error}
        </p>
      ) : null}

      <div className="scroll-thin min-h-0 flex-1 overflow-y-auto px-3.5 pb-2">
        {messages.length === 0 ? (
          <p className="py-2 text-[10.5px] leading-relaxed text-white/45">
            Describe the mission you want. The operator reads the fleet, the zones and the weather,
            then proposes routes here for you to check on the map. Nothing reaches an aircraft
            until you approve it.
          </p>
        ) : (
          <div className="flex flex-col gap-1.5">
            {messages.map((message, index) =>
              message.role === 'user' ? (
                <div
                  key={index}
                  className="ml-6 self-end rounded-[12px] bg-white/10 px-3 py-2 text-[11px] leading-relaxed"
                >
                  {message.content}
                </div>
              ) : (
                <div
                  key={index}
                  className="mr-2 rounded-[12px] border px-3 py-2.5"
                  style={{
                    borderColor: message.error
                      ? `color-mix(in oklch, ${DANGER} 45%, transparent)`
                      : 'rgba(255,255,255,0.10)',
                    background: 'rgba(0,0,0,0.22)',
                  }}
                >
                  <p className="whitespace-pre-wrap text-[11px] leading-relaxed">
                    {message.content}
                  </p>
                  <StepTrace message={message} />
                </div>
              ),
            )}
            {busy ? <WorkingFor steps={liveSteps} /> : null}
            <div ref={endRef} />
          </div>
        )}

        {proposal ? (
          <div className="mt-2">
            <ProposalReview
              proposal={proposal}
              steps={stepsFor(messages, proposal.proposalId)}
              onEdit={() => {
                setInput('Revise this proposal: ');
                inputRef.current?.focus();
              }}
            />
          </div>
        ) : null}
      </div>

      <div className="flex flex-none gap-1.5 px-3.5 pb-2">
        <input
          ref={inputRef}
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') send();
          }}
          aria-label="Message the AI mission operator"
          placeholder="Search the valley with two aircraft…"
          className="well h-8 min-w-0 flex-1 rounded-[99px] px-3 text-[11px] outline-none placeholder:text-white/35"
        />
        {voice.supported ? (
          <button
            type="button"
            aria-label="Hold to talk"
            title="Hold to dictate. Dictation writes into the message box; it can never approve."
            onPointerDown={() => voice.start()}
            onPointerUp={() => voice.stop()}
            onPointerLeave={() => voice.stop()}
            onKeyDown={(event) => {
              if (event.key === ' ' || event.key === 'Enter') voice.start();
            }}
            onKeyUp={(event) => {
              if (event.key === ' ' || event.key === 'Enter') voice.stop();
            }}
            className="rounded-full border border-white/15 px-3 font-mono text-[9px] font-bold"
            style={voice.listening ? { color: DANGER } : { color: 'rgba(244,244,245,0.6)' }}
          >
            {voice.listening ? '● REC' : 'HOLD'}
          </button>
        ) : null}
        <button
          type="button"
          onClick={send}
          disabled={busy || !input.trim()}
          className="rounded-full border border-white/15 px-3 font-mono text-[9px] font-bold text-white/70 disabled:opacity-40"
        >
          SEND
        </button>
      </div>

      {voice.error ? (
        <p className="flex-none px-3.5 pb-1.5 text-[9.5px]" style={{ color: WARN }}>
          Dictation: {voice.error}
        </p>
      ) : null}

      <p className="flex-none px-3.5 pb-3 text-[9px] leading-relaxed text-white/35">
        Dictation writes into the message box only. Approving, rejecting and releasing are clicks —
        for real aircraft, a click plus the callsigns typed.{' '}
        <span style={{ color: OK }}>Nothing here reaches an airframe without that.</span>
      </p>
    </aside>
  );
}

export default CopilotDock;
